import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  CardContent,
  Avatar,
  Typography,
  Box,
  Chip,
  Grid // ✅ ADD
} from '@mui/material';
import CakeIcon from '@mui/icons-material/Cake';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';

/* ---------------- CONFETTI ---------------- */

const Confetti = () => {
  const colors = ['#ff4081', '#7c4dff', '#4caf50', '#ff9800', '#03a9f4'];

  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
        zIndex: 1
      }}
    >
      {[...Array(25)].map((_, i) => (
        <Box
          key={i}
          sx={{
            position: 'absolute',
            width: 8,
            height: 14,
            backgroundColor: colors[i % colors.length],
            top: -20,
            left: `${Math.random() * 100}%`,
            opacity: 0.9,
            borderRadius: 1,
            animation: `confetti-fall ${3 + Math.random() * 3}s linear infinite`,
            animationDelay: `${Math.random() * 2}s`
          }}
        />
      ))}

      <style>
        {`
          @keyframes confetti-fall {
            0% { transform: translateY(0) rotate(0deg); }
            100% { transform: translateY(280px) rotate(360deg); }
          }
        `}
      </style>
    </Box>
  );
};

/* ---------------- CARD ---------------- */

const WishesCard = ({
  birthdayData = [],
  anniversaryData = [],
  birthdayImage, // Specific image for birthday
  anniversaryImage, // Specific image for anniversary
  defaultImage, // Specific image for default
  isHalf = false,
  isLoading = false
}) => {
  const [activeIndex, setActiveIndex] = useState(0);

  // Combine datasets directly without date checking
  const validEvents = useMemo(() => {
    console.log('WishesCard Props:', { birthdayData, anniversaryData });

    const parseData = (data, type) => {
      if (!data) return [];

      if (Array.isArray(data)) {
        return data.map((item) => ({ ...item, type }));
      }

      if (typeof data === 'object') {
        return [{ ...data, type }];
      }

      return [];
    };

    const bdays = parseData(birthdayData, 'birthday');
    const anniversaries = parseData(anniversaryData, 'anniversary');

    const combined = [...bdays, ...anniversaries];
    console.log('WishesCard Computed Events:', combined);
    return combined;
  }, [birthdayData, anniversaryData]);

  // Rotation Logic
  useEffect(() => {
    if (validEvents.length > 1) {
      const timer = setInterval(() => {
        setActiveIndex((prev) => (prev + 1) % validEvents.length);
      }, 5000); // Rotate every 5 seconds
      return () => clearInterval(timer);
    } else {
      setActiveIndex(0);
    }
  }, [validEvents.length]);

  if (isLoading) return null;

  const getGradient = (employeeName = '') => {
    const gradients = [
      'linear-gradient(135deg, #667eea, #764ba2)',
      'linear-gradient(135deg, #ff758c, #ff7eb3)',
      'linear-gradient(135deg, #43cea2, #185a9d)',
      'linear-gradient(135deg, #f7971e, #ffd200)',
      'linear-gradient(135deg, #36d1dc, #5b86e5)'
    ];

    return gradients[employeeName.length % gradients.length];
  };

  // If no events today, show default card
  if (validEvents.length === 0) {
    return (
      <Grid item xs={12} md={isHalf ? 6 : 12}>
        <Card
          sx={{
            display: 'flex',
            alignItems: 'stretch',
            borderRadius: 4,
            overflow: 'hidden',
            marginBottom: '2rem',
            height: 260,
            position: 'relative',
            backgroundImage: `url(${defaultImage})`,
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            filter: 'grayscale(100%)',
            transition: 'all 0.35s ease'
          }}
        >
          {/* Empty Box as requested */}
          <Box sx={{ p: 3, zIndex: 2, display: 'flex', alignItems: 'center', height: '100%' }} />
        </Card>
      </Grid>
    );
  }

  // Identify Current Event
  const currentEvent = validEvents[activeIndex];
  const { employeeName, designation, profileImage, type } = currentEvent;
  const isBday = type === 'birthday';

  // Construct proper image URL from base64 data or URL
  const imageUrl = profileImage
    ? profileImage.startsWith('data:') || profileImage.startsWith('http')
      ? profileImage
      : `data:image/png;base64,${profileImage}`
    : '';

  // Determine Background Image
  let bgImage = defaultImage;
  if (isBday) bgImage = birthdayImage;
  else bgImage = anniversaryImage;

  return (
    <Grid item xs={12} md={isHalf ? 6 : 12}>
      <Card
        sx={{
          display: 'flex',
          alignItems: 'stretch',
          borderRadius: 4,
          overflow: 'hidden',
          marginBottom: '2rem',
          height: 260,
          position: 'relative',
          width: '100%',
          backgroundImage: `url(${bgImage})`, // Use dynamic image
          backgroundSize: isBday ? 'cover' : '442px 262px',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: isBday ? 'center' : 'right',
          transition: 'all 0.35s ease'
        }}
      >
        {isBday && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(255,255,255,0.39)',
              zIndex: 0
            }}
          />
        )}

        {isBday && <Confetti />}

        <Box
          sx={{
            width: 10,
            zIndex: 2,
            background: isBday ? '' : 'linear-gradient(180deg, #11998e, #38ef7d)'
          }}
        />

        <Box
          sx={{
            px: 3,
            display: 'flex',
            alignItems: 'center',
            position: 'relative',
            zIndex: 2
          }}
        >
          <Avatar
            src={imageUrl}
            alt={employeeName}
            sx={{
              width: 246,
              height: 246,
              border: '4px solid white',
              boxShadow: '0 8px 20px rgba(0,0,0,0.3)',
              background: imageUrl ? 'transparent' : getGradient(employeeName),
              color: '#fff',
              fontSize: 80,
              fontWeight: 800
            }}
          >
            {!imageUrl &&
              employeeName
                ?.split(' ')
                .map((n) => n[0])
                .join('')
                .toUpperCase()}
          </Avatar>

          <Box
            sx={{
              position: 'absolute',
              bottom: 10,
              right: 60,
              width: 34,
              height: 34,
              borderRadius: '50%',
              background: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 10px rgba(0,0,0,0.25)'
            }}
          >
            {isBday ? <CakeIcon fontSize="small" color="primary" /> : <WorkspacePremiumIcon fontSize="small" color="primary" />}
          </Box>
        </Box>

        <CardContent sx={{ flex: 1, pr: 3, zIndex: 2 }}>
          <Chip
            label={isBday ? 'Birthday Wishes 🎉' : 'Work Anniversary 🎖️'}
            sx={{
              mb: 1.5,
              fontWeight: 600,
              backgroundColor: '#eef2ff',
              color: '#3f51b5'
            }}
          />

          <Typography variant="h4" fontWeight={800}>
            {employeeName}
          </Typography>

          <Typography variant="h6" sx={{ color: '#555', mb: 1 }}>
            {designation}
          </Typography>

          <Typography variant="body1" sx={{ color: '#333', lineHeight: 1.7 }}>
            {isBday
              ? 'Wishing you a very happy birthday and a year filled with success, happiness, and good health.'
              : 'Congratulations on your work anniversary.'}
          </Typography>

          <Typography variant="body1" sx={{ color: '#333', lineHeight: 1.7 }}>
            {isBday ? '' : 'Thank you for your dedication and continued contributions.'}
          </Typography>
        </CardContent>
      </Card>
    </Grid>
  );
};

export default WishesCard;
